import React, { useMemo, useState } from 'react';
import { Form } from 'lib/components/Form';
import { ESnapshotExists } from 'lib/types';
import { OrderInvoice, PublicNoticeInvoice } from 'lib/types/invoices';
import { CardGridLayout, GridInput } from 'lib/components/Card/Grid';
import { getCurrencySymbol } from 'lib/helpers';
import { TextField } from 'lib/components/TextField';
import { Modal } from 'lib/components/Modal';
import api from 'api';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { ColumnService } from 'lib/services/directory';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { ColumnButtonProps } from 'lib/components/ColumnButton';
import { LoadingSpinner } from 'lib/components/LoadingSpinner';
import { InvoiceRefundModalNoticeTable } from './InvoiceRefundModalNoticeTable';
import { InvoiceRefundModalOrderTable } from './InvoiceRefundModalOrderTable';
import {
  RefundUIAdapter,
  RefundUIAdapterArgs,
  RefundEntityContext,
  RefundPermissions,
  PerformRefundContext
} from './types';

export interface InvoiceRefundModalProps {
  adapter: RefundUIAdapter;
  entity: RefundEntityContext;
  invoice: ESnapshotExists<PublicNoticeInvoice> | ESnapshotExists<OrderInvoice>;
  permissions: RefundPermissions;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: () => void;
}

export function InvoiceRefundModal({
  adapter,
  entity,
  invoice,
  permissions,
  onClose,
  onSuccess,
  onError
}: InvoiceRefundModalProps) {
  const adapterArgs: RefundUIAdapterArgs = { entity, invoice, permissions };
  const ui = adapter.buildUIConfig(adapterArgs);
  const lineItemsDiff = adapter.getLineItemsDiff(adapterArgs);
  const defaultRefund = adapter.getDefaultRefundAmount(adapterArgs);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState(defaultRefund);
  const [refundAmountInput, setRefundAmountInput] = useState(
    defaultRefund.toFixed(2)
  );
  const [loading, setLoading] = useState(false);

  const displayItems = useMemo(
    () => adapter.getDisplayItems?.(adapterArgs) ?? [],
    [adapter, adapterArgs]
  );
  const [perItemInputs, setPerItemInputs] = useState<Record<string, string>>(
    {}
  );

  const { value: priorRefundLineItems, isLoading: isLoadingPriorRefunds } =
    useAsyncEffect({
      fetchData: async () => {
        const result =
          await api.safeGetWithParams<'invoice-transactions/:invoiceId/refund-transactions'>(
            `invoice-transactions/${invoice.id}/refund-transactions`
          );
        if ('success' in result && result.success) {
          return result.refundLineItems;
        }
        return [];
      },
      initialData: [],
      dependencies: [invoice.id],
      errorConfig: {
        service: ColumnService.PAYMENTS,
        message: 'Error loading invoice refund modal',
        tags: { invoiceId: invoice.id || '' }
      }
    });

  const totalRefunded =
    priorRefundLineItems?.reduce((a, li) => a + li.amount, 0) || 0;
  const total = invoice.data().pricing?.totalInCents || 0;
  const maxRefundAmount = Math.max(0, (total - totalRefunded) / 100);

  // In per-item mode, compute total from per-row inputs; otherwise use single refundAmount
  const perItemRefundSum = useMemo(() => {
    if (!ui.perItemRefund) return 0;
    return displayItems.reduce((acc, item, idx) => {
      const key = item.id || String(idx);
      const raw = perItemInputs[key] ?? '0';
      const parsed = parseFloat(raw);
      return acc + (isNaN(parsed) ? 0 : parsed);
    }, 0);
  }, [displayItems, perItemInputs, ui.perItemRefund]);
  const currentRefundAmount = ui.perItemRefund
    ? perItemRefundSum
    : refundAmount;

  const isFullRefund = currentRefundAmount === maxRefundAmount;
  const refundAmountExceedsMax = currentRefundAmount > maxRefundAmount;
  const nullRefundAmount = currentRefundAmount <= 0;
  const currency = getCurrencySymbol(entity.currency);

  const disablePrimary =
    loading ||
    ui.refundDisabled ||
    !refundReason ||
    refundAmountExceedsMax ||
    nullRefundAmount;
  const disableSecondary =
    disablePrimary || !isFullRefund || !ui.performVoidAndRefund;

  const invoiceLineItems = invoice.data().inAppLineItems;
  const formId = 'refund-invoice-form';
  const refundButtonAction: ColumnButtonProps = {
    buttonText: 'Refund Amount',
    id: 'refund-amount',
    formId,
    type: 'submit' as const,
    destructive: true,
    disabled: disablePrimary,
    primary: true,
    loading
  };
  const performVoidAndRefundAction: ColumnButtonProps = {
    buttonText: 'Void Invoice and Refund Full Amount',
    type: 'button' as const,
    disabled: disableSecondary,
    onClick: () => {
      if (disableSecondary || !ui.performVoidAndRefund) return;
      setLoading(true);
      const refundCtx: PerformRefundContext = {
        args: adapterArgs,
        reason: refundReason,
        totalAmountInCents: Math.round(maxRefundAmount * 100),
        fullRefund: true
      };
      void (async () => {
        try {
          await ui.performVoidAndRefund!(refundCtx);
          onClose();
          onSuccess?.();
        } catch {
          onError?.();
        } finally {
          setLoading(false);
        }
      })();
    }
  };

  // Build primary actions (right-aligned). If void+refund available, include both.
  const primaryActions = ui.performVoidAndRefund
    ? [performVoidAndRefundAction, refundButtonAction]
    : refundButtonAction;

  return (
    <Modal
      id="refund-invoice-modal"
      onClose={onClose}
      title="Process a Refund"
      size="6xl"
      primaryAction={primaryActions}
    >
      <Form
        id={formId}
        onSubmit={e => {
          e.preventDefault();
          if (loading) return;
          setLoading(true);
          const totalAmountInCents = Math.round(currentRefundAmount * 100);
          const refundCtx: PerformRefundContext = {
            args: adapterArgs,
            reason: refundReason,
            totalAmountInCents,
            fullRefund: isFullRefund
          };
          if (ui.perItemRefund) {
            const items = displayItems
              .map((item, idx) => {
                const key = item.id || String(idx);
                const raw = perItemInputs[key] ?? '0';
                const dollars = parseFloat(raw);
                const amountInCents = isNaN(dollars)
                  ? 0
                  : Math.round(dollars * 100);
                return {
                  id: item.id,
                  amountInCents,
                  description: item.description
                };
              })
              .filter(i => i.amountInCents > 0);
            refundCtx.items = items;
          }
          void (async () => {
            try {
              await ui.performRefund(refundCtx);
              onClose();
              onSuccess?.();
            } catch {
              onError?.();
            } finally {
              setLoading(false);
            }
          })();
        }}
      >
        <div className="flex flex-col lg:flex-row gap-4 max-h-[70vh]">
          <div className="flex-1 overflow-y-auto">
            <CardGridLayout
              header={{
                title: 'Paid Invoice Review',
                description: adapter.buildDisplaySubtitle(adapterArgs)
              }}
            >
              <>
                {isLoadingPriorRefunds && <LoadingSpinner />}
                {!isLoadingPriorRefunds && ui.perItemRefund && (
                  <GridInput fullWidth>
                    <InvoiceRefundModalOrderTable
                      currency={currency}
                      displayItems={displayItems}
                      perItemInputs={perItemInputs}
                      onPerItemInputChange={(id, value) =>
                        setPerItemInputs(prev => ({ ...prev, [id]: value }))
                      }
                      refunds={priorRefundLineItems}
                      totalRefundedInCentsFromPriorInvoiceTransactions={
                        totalRefunded
                      }
                    />
                  </GridInput>
                )}
                {!isLoadingPriorRefunds &&
                  !ui.perItemRefund &&
                  adapterArgs.entity.kind === 'notice' && (
                    <InvoiceRefundModalNoticeTable
                      rate={adapterArgs.entity.rate}
                      invoice={invoice}
                      currency={entity.currency}
                      timezone={entity.timezone}
                      baseDistributeFeeSetting={
                        adapterArgs.entity.orgDistributeFeeSetting
                      }
                      refunds={priorRefundLineItems}
                      totalRefundedInCentsFromPriorInvoiceTransactions={
                        totalRefunded
                      }
                      lineItemsDiff={lineItemsDiff}
                      invoiceLineItems={invoiceLineItems}
                    />
                  )}
                {refundAmountExceedsMax && (
                  <GridInput fullWidth>
                    <p className="text-column-red-500 text-xs">
                      You cannot refund the advertiser for more than the
                      subtotal of this invoice.
                    </p>
                  </GridInput>
                )}
              </>
            </CardGridLayout>
          </div>
          <div className="w-full lg:max-w-sm">
            <CardGridLayout
              header={{
                title: ui.header,
                description: ui.body
              }}
            >
              {!ui.perItemRefund && (
                <GridInput fullWidth>
                  <TextField
                    id="refund-subtotal-amount"
                    labelText="Refund Amount *"
                    type="currency"
                    value={refundAmountInput}
                    step={0.01}
                    max={loading ? undefined : maxRefundAmount || undefined}
                    onChange={value => {
                      setRefundAmountInput(value);
                      const parsedValue = parseFloat(value);
                      if (!isNaN(parsedValue)) {
                        setRefundAmount(parsedValue);
                      }
                    }}
                    validationMessages={{
                      rangeOverflow:
                        'You cannot refund the advertiser for more than the subtotal of this invoice.'
                    }}
                  />
                </GridInput>
              )}
              <GridInput fullWidth>
                <TextField
                  id="refund-invoice-reason"
                  labelText="Reason for refund"
                  placeholder="Reason for refund"
                  value={refundReason}
                  onChange={setRefundReason}
                  required
                  validationMessages={{
                    valueMissing:
                      'Please specify a reason for refunding or voiding this invoice.'
                  }}
                />
              </GridInput>
              <GridInput fullWidth>
                {!refundAmountExceedsMax && !nullRefundAmount && (
                  <div className="flex flex-row items-center text-sm rounded-md align-middle min-h-11 w-full space-x-2 py-1.5 pr-3 mt-3 bg-column-green-50">
                    <ExclamationCircleIcon className="text-column-green-500 h-16 w-16 pl-3" />
                    <div className="text-column-green-500">
                      The advertiser will receive a total refund of {currency}
                      {currentRefundAmount.toFixed(2)}.
                    </div>
                  </div>
                )}
              </GridInput>
            </CardGridLayout>
          </div>
        </div>
      </Form>
    </Modal>
  );
}
